import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, throwError, catchError, concatMap, delay, retryWhen } from 'rxjs';
import { RouterConstants } from './constants/router-constants';
import { SplunkLogsService } from './services/splunkLogs.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationComponent } from './confirmation/confirmation.component';


@Injectable()
export default class ApiInterceptor implements HttpInterceptor {
    customError: any = { error: {} };
    modalRefBox: any;
    apiErrorMessage = "An error occurred while processing your request. \n Please try again later. If the issue continues, contact support for assistance."
    failedRequests = new Set<string>();
    constructor(
        private routerConstant: RouterConstants,
        private splunkLogsService: SplunkLogsService,
        private oauthService: OAuthService,
        private router: Router,
        private modalService: NgbModal,
    ) { }

    retryCount: any = 3;
    private static readonly REAUTH_RELOAD_GUARD_MS = 30000;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if session is expired or API calls should be blocked


        const sessionStateStr = window.localStorage.getItem('sessionStateBeforeDisconnect');
        let sessionState: { isSessionExpired?: boolean } | null = null;
        if (sessionStateStr) {
            try {
                sessionState = JSON.parse(sessionStateStr);
            } catch {
                sessionState = null;
            }
        }

        if (sessionState?.isSessionExpired) {
            return throwError(() => new HttpErrorResponse({
                status: 401,
                error: { errorMsg: 'Session Expired' }
            }));
        }
        const requestKey = `${req.method}:${req.url}`;
        const ssoUrl = window['__env'].ssoUrl;

        const loggedInUser = this.routerConstant.getLoggedInUser();
        const ipifyUrl = req.url.split('/');
        //const modifiedUrl = req?.url.includes("https://int-id.cisco.com") ? req.url : (window.location.hostname.includes('localhost') ? `http://localhost:8080/gsmscw/api${req.url}` : `/gsmscw/api${req.url}`);
        const modifiedUrl = req.url;
        const authToken = this.oauthService.getAccessToken();//window.localStorage.getItem('adata');
        let cloneReq;

        const regionCode = window.localStorage.getItem('regionCode');
        const countryCode = window.localStorage.getItem('countryCode');
        const continentCode = window.localStorage.getItem('continentCode');

        const isExternalLookup = (ipifyUrl[2] === 'jsonip.com') || (ipifyUrl[2] === 'ipapi.co');
        const isSsoRequest = !!ssoUrl && !!req?.url.includes(ssoUrl);
        // SCS-128292: the page can be loaded from saved session data while the SSO access token is
        // gone (cleared in another tab, expired and not refreshed, ...). Every backend call needs a
        // Bearer token and is rejected with "Invalid Authorization Header" without one, so do not
        // send it: fail as an expired session and re-authenticate through SSO instead.
        if (!authToken && !isExternalLookup && !isSsoRequest && !this.isPublicRoute()) {
            this.reauthenticate();
            return throwError(() => new HttpErrorResponse({
                status: 401,
                error: { errorMsg: 'Session Expired' }
            }));
        }

        if (isExternalLookup) {
            cloneReq = req.clone({
                url: req.url
            });
        } else {
            cloneReq = req.clone({
                setHeaders: {
                    emailId: loggedInUser ? loggedInUser.email : '',
                    userId: loggedInUser ? loggedInUser.userId : '',
                    role: loggedInUser ? loggedInUser.role.value : '',
                    regionCode: regionCode ? regionCode : '',
                    countryCode: countryCode ? countryCode : '',
                    continentCode: continentCode ? continentCode : '',
                    //Authorization:  (req?.url.includes("https://int-id.cisco.com")) ? '' : (authToken ? `Bearer ${this.oauthService.getAccessToken()}` : ''),
                    Authorization: (req?.url.includes(ssoUrl)) ? '' : (authToken ? `Bearer ${this.oauthService.getAccessToken()}` : ''),

                    isWhitepaperAdmin: loggedInUser ? loggedInUser.isWhitepaperAdmin : 'N'
                    // Authorization:  req?.url.includes("https://int-id.cisco.com") ? '' : (authToken ? `Bearer ${this.encDecService.get(authToken)}` : '')
                },
                url: modifiedUrl
            });
        }
        const apiReq = cloneReq;

        return next.handle(apiReq).pipe(
            retryWhen(errors =>
                errors.pipe(
                    concatMap((error, count) => {
                        if (count < this.retryCount && error.status === 504) {
                            return of(error);
                        }
                        return throwError(error);
                    }),
                    delay(500)
                )
            ),
            catchError((err: any) => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    // Parse the error message to check the isInternal flag
                    let errorMessage = err.error.errorMsg;
                    let parsedError: any;
                    try {
                        parsedError = JSON.parse(errorMessage); // Parse the JSON string
                        // Check if isInternal is present in the parsed object
                        const isInternal = parsedError?.isInternal === "true";
                        window.localStorage.clear();
                        window.localStorage.setItem('isInternal', isInternal.toString());
                        this.router.navigate(['/no-cepm-role']);
                    } catch (e) {
                        // Fallback to default behavior if parsing fails
                        this.oauthService.refreshToken().catch(() => {
                            // Handle refresh token failure if needed
                        });
                    }
                }
                else if (err instanceof HttpErrorResponse && err.status === 504) {
                    // server-side error
                    this.addSplunkLog(err, req);
                    //this.customError.error.errorMsg = 'Connection Timed Out. Kindly reload the page to view data.';
                    this.customError.error.errorMsg = "";
                    setTimeout(() => {
                        const errorMsgElementsContent = document.querySelectorAll('.modal-content');
                        const errorMsgElementsDialog = document.querySelectorAll('.modal-dialog');
                        errorMsgElementsDialog.forEach((element) => {
                            element.classList.add('new-error-class');
                            (element as HTMLElement).style.maxWidth = '700px';
                        });
                        errorMsgElementsContent.forEach((element) => {
                            element.classList.add('new-error-class-content');
                            (element as HTMLElement).style.height = '500px';
                        });

                        let errMessage = document.getElementsByClassName('line-broken-msg') as HTMLCollection;
                        if (errMessage.length > 0) {
                            errMessage[0].innerHTML = "<p>We've attempted to refresh the page several times, but it seems there's still an issue.</p>"
                                + "<p>We apologize for the inconvenience and appreciate your patience. Here's what you can do next:</p>"

                                + "<ol><li><strong>Check Your Connection:</strong> Ensure your internet connection is stable and active.</i>"

                                + "<li><strong>Clear Your Browser Cache:</strong> Sometimes old data can cause loading issues. Clearing your browser cache may resolve this problem.</li>"

                                + "<li><strong>Try a Different Browser:</strong> Occasionally, browser-specific issues can affect page loading. If possible, open the application in a different web browser.</li>"

                                + "<li><strong>Disable Browser Extensions:</strong> Some browser extensions can interfere with website functionality. Try disabling them to see if that helps.</li>"

                                + "<li><strong>Visit Later:</strong> We might be experiencing high traffic or technical difficulties. You may want to try again in a few minutes.</li></ol>"

                                + "<p>If you need immediate assistance or if the issue persists, please contact our support team at <a href='mailto:scw_support@cisco.com'>[scw_support@cisco.com]</a></p>"

                                + "We're here to help!";
                        }

                    }, 300);
                    return throwError(this.customError);
                } else if (err instanceof HttpErrorResponse && (err.status === 500 || err.status === 400 || err.status === 403 || err.status === 404 || err.status === 502 || err.status === 503)) {
                    // Add failed request to set to prevent duplicate calls
                    if (err.status === 403 && req.url.includes('cepmService/cepmRolesResources')) {
                        this.failedRequests.add(requestKey);
                    }                    
                    if (this.router.url !== '/logout' && !apiReq.url.includes('/whitepaper/publishWhitepaper')) {
                        if (!modifiedUrl.includes('/supplierCommodityMapping/commodity-contact-update')) {
                            this.openConfirmationPopup('Error', this.apiErrorMessage, false);
                        }
                        // this.openConfirmationPopup('Error', this.apiErrorMessage, false);
                    }
                    return throwError(() => err);
                }
            })
        );
    }

    /** Routes that are reachable without being signed in (mirrors AuthService.initAuth). */
    private isPublicRoute(): boolean {
        return ['/logout', '/probe', '/no-role-external', '/callback'].includes(window.location.pathname);
    }

    /**
     * Restarts the app so AuthService.initAuth() sends the user through SSO login and back to the
     * page they were on. Guarded so a login that keeps coming back without a token cannot cause a
     * reload loop.
     */
    private reauthenticate(): void {
        const key = 'scwMissingTokenReloadAt';
        const lastReload = Number(window.sessionStorage.getItem(key) || 0);
        if (Date.now() - lastReload < ApiInterceptor.REAUTH_RELOAD_GUARD_MS) {
            return;
        }
        window.sessionStorage.setItem(key, String(Date.now()));
        this.reloadPage();
    }

    protected reloadPage(): void {
        window.location.reload();
    }

    addSplunkLog(err: any, req: any) {
        const requestObj: any = {
            subLogType: 'gatewayTimeout',
            message: 'RequestTimeout',
            errorCode: err.status,
            serviceName: req.url
        };
        this.splunkLogsService.additionalLogs(requestObj).subscribe();
    }

    openConfirmationPopup(title: any, msg: any, flag: boolean) {
        this.modalRefBox = this.modalService.open(ConfirmationComponent, {
            centered: true,
            windowClass: 'alertModalWindowClass',
            backdrop: 'static',
            keyboard: false,
        });
        this.modalRefBox.componentInstance.title = title;
        this.modalRefBox.componentInstance.message = msg;
        this.modalRefBox.componentInstance.showCancelButton = flag;
    }
}